import { useCallback, useMemo } from 'react';

import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Language, mapFromApiCode, getLanguages } from '@services/languages';

const QUERY_KEY = 'languages';

export const useLanguages = () => {
  const { i18n } = useTranslation();

  const {
    data: languages,
    isLoading,
    error,
  } = useQuery<Language[]>(QUERY_KEY, getLanguages, {
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
  });

  const currentLanguage = useMemo(() => {
    if (!languages) return null;

    const currentCode = i18n.language;
    // Procura pelo código atual ou pelo mapeado
    return languages.find(
      (lang) =>
        lang.code === currentCode || mapFromApiCode(lang.code) === currentCode,
    );
  }, [languages, i18n.language]);

  const changeLanguage = useCallback(
    (code: string) => {
      // O i18n usa os códigos legados (br, en)
      const legacyCode = mapFromApiCode(code);
      i18n.changeLanguage(legacyCode);
    },
    [i18n],
  );

  const defaultLanguage = useMemo(
    () => languages?.find((lang) => lang.is_default) || null,
    [languages],
  );

  return {
    languages: languages || [],
    currentLanguage,
    defaultLanguage,
    isLoading,
    error,
    changeLanguage,
  };
};

export default useLanguages;
